import { normalizeAccountability } from './accountability';
import { integrationPathFor } from '../data/buildScripts';
/* The same predicate the console's Connect step branches on — one answer to
   "is this framework coded-SDK", not a second list to keep in step. */
import { isCodedFramework } from '../components/v2/frameworkCatalog';
import {
  gelOnboardExteLet,
  autoCreateAgent,
  createAgent,
  recomputeAgentDesignTimeScore,
} from './api';

/**
 * The Smart Agent Wizard's dispatcher: decide the archetype, then call the one
 * backend path that produces it.
 *
 * <h3>Why this exists</h3>
 *
 * Archetype is not a field you set. `Agent.getArchetype()` is derived on every
 * read from four flag columns (`Agent.java:795-805`), and those flags are only
 * written by the endpoint that creates the agent. So the archetype IS the
 * endpoint:
 *
 *   Let     → POST /api/v1/agents
 *   AutoLet → POST /api/v1/auto-create
 *   ExteLet → POST /api/v1/gel/registrations/onboard
 *
 * Driving everything through one of them — which is what the wizard did before
 * this module — produces the wrong kind of agent no matter what the tile said.
 * `AgentsInventoryPageV2.jsx:402-410` has the same bug from the other
 * direction: it posts `isExternal`/`isSystemAgent` to `POST /api/v1/agents`,
 * whose DTO is `ignoreUnknown=true`, so Jackson drops them and every archetype
 * picked there silently becomes a plain Let.
 *
 * <h3>Managed mode</h3>
 *
 * An ExteLet onboard needs an ACTIVE `platformConnectorId` or it is a 400, and
 * a first-time visitor has no connectors. So the wizard provisions one: it
 * looks for an ACTIVE connector of the right platform type and creates one if
 * there is none. That is the whole difference between "click Cowork agent and
 * it works" and sending someone to go and configure a connector first.
 *
 * The connector is reused once it exists, because a connector is a connection to a
 * platform and one is all a tenant needs. The agent is not. Every pick creates an
 * agent: its own row, its own SPIFFE identity, its own gateway credential scoped to
 * it alone, assigned to whoever created it. Two people picking the same tile get two
 * agents, exactly as two people creating anything else here get two of it.
 */

/** Connector status that `onboard` will accept (`PlatformConnectorEntity:172`). */
/**
 * The value the platform records as this agent's origin.
 *
 * Mirrors `Agent.SOURCE_SMART_WIZARD` exactly. The backend only honours a value it
 * recognises (`ExternalAgentRegistrationService.resolveCreationSource`), so a typo
 * here does not corrupt the inventory facet — it silently falls back to MANUAL,
 * which is why the constant lives in one place on each side rather than inline at
 * each call.
 */
const CREATION_SOURCE = 'SMART_WIZARD';

/** How the name suffix is separated from the agent's own name. */
const SUFFIX_SEPARATOR = ' · ';

/**
 * A short tag for whoever is creating this agent, appended to every name.
 *
 * Three letters of their first name, upper-cased, letters only. Short enough not to
 * crowd a tile name in a list, long enough that two people in the same tenant can
 * tell their agents apart at a glance — which is the whole point, because the
 * inventory shows one row per agent and "Cowork agent" three times over tells
 * nobody anything.
 *
 * Returns null rather than a placeholder when there is no name to read. A suffix of
 * "UNK" would be worse than none: it looks like a person.
 */
export function actorTag(displayName) {
  const first = String(displayName || '').trim().split(/\s+/)[0] || '';
  const letters = first.replace(/[^A-Za-z]/g, '');
  return letters.length >= 2 ? letters.slice(0, 3).toUpperCase() : null;
}

/**
 * The agent's name as it will appear in the inventory.
 *
 * Always suffixed when a tag is available — not only on a collision. A name that
 * sometimes carries a tag and sometimes does not is a name nobody can predict, and
 * the first person to create one would get the bare name while everybody after them
 * got a decorated one, which reads as a defect rather than a convention.
 *
 * Already-suffixed names are left alone, so a name that arrives carrying a tag — from
 * a caller that built it, or from an agent being named after an existing one — does
 * not accumulate a second.
 */
export function displayNameFor(baseName, actorName) {
  const base = String(baseName || '').trim();
  const tag = actorTag(actorName);
  if (!base || !tag) return base;
  if (base.endsWith(`${SUFFIX_SEPARATOR}${tag}`)) return base;
  return `${base}${SUFFIX_SEPARATOR}${tag}`;
}

/**
 * Narration for the live lane.
 *
 * The wizard's right-hand lane shows what the platform is doing, and for an AutoLet
 * that feed comes from the pipeline's own event stream. An ExteLet has no stream:
 * `onboard` is one synchronous call that does several distinct things. So the steps
 * are emitted here, from the client, and only ever AFTER the work they describe has
 * returned — a step that announces an intention would be a progress bar, and this
 * lane is supposed to be evidence.
 *
 * A step carries the same shape the streamed feed uses, so `ProveLane` renders both
 * without knowing which produced it.
 */
function narrator(onStep) {
  let n = 0;
  return (text, opts = {}) => {
    if (typeof onStep !== 'function') return;
    n += 1;
    try {
      onStep({
        id: `wiz-${n}`, at: null,
        // The trail draws a node per step; a client step is a title like any other.
        title: text, text: opts.detailText || null, detail: opts.detail || null,
        /* Which journey stage this piece of work cleared. The strip shows where
           the AGENT is, and it can only follow the work if the work says so. */
        stage: opts.stage || null,
        /* The Trust Score travels as a number, not inside the sentence. The
           trail draws it as a wheel; parsing it back out of the prose would
           break the first time the wording changed. */
        score: typeof opts.score === 'number' ? opts.score : null,
        band: opts.band || null,
        refused: Boolean(opts.refused), tone: opts.refused ? 'deny' : null,
      });
    } catch {
      // The lane is a view. A render that throws must not take the agent down with it.
    }
  };
}


/**
 * Ask the platform to score the agent it has just brought under governance.
 *
 * Best-effort and last. The agent exists and is governed whether or not a score
 * comes back, so a failure here is narrated and swallowed rather than thrown: the
 * person asked for an agent, not for a number, and losing the number must not look
 * like losing the agent.
 *
 * The score is low on a fresh agent and that is the correct reading — nothing is
 * bound to it yet, nothing has probed it. Saying so is the point of showing it: the
 * number is what the remaining work is measured against.
 */
async function scoreNow(agentId, say) {
  try {
    const res = await recomputeAgentDesignTimeScore(agentId);
    const body = res?.data || res;
    if (body?.score == null) {
      say('Not enough on this agent to score it yet — bind a policy or run a probe '
        + 'and its Trust Score will appear.');
      return null;
    }
    const band = String(body.band || '').replace(/^./, (c) => c.toUpperCase());
    /* Register: the design-time score is what the registration produced, and it
       is read before any of the later stages have run. */
    say(`Scored at design time, on ${body.factors?.length || 0} `
      + 'of the six factors the published method defines.',
    { score: body.score, band, stage: 'discovery' });
    return body;
  } catch (e) {
    say('Could not score this agent just now. It is protected either way — the score '
      + 'can be recomputed from its Trust Centre.', { refused: true });
    return null;
  }
}

/** An external agent that already runs elsewhere: register it and govern it. */
async function provisionExteLet(tile, actorName, say, accountability, build = {}) {
  /* Checked before the connector is touched. A nomination that names nobody is
     refused here rather than sent: the backend would leave the record
     DISCOVERED_UNOWNED (OwnershipRepresentationService:96-98) and the surface
     would go on to report a nomination the record does not contain. */
  const intent = normalizeAccountability(accountability);
  const name = displayNameFor(build.agent?.name || tile.name, actorName);
  /* The framework the person chose decides the platform type; the tile's own is
     the fallback for callers that never asked. */
  /* The connection was chosen — and, if the customer added their own, created
     and health-checked — on the spine. Nothing is conjured here: binding to a
     connector this surface invented is the divergence from the console's own
     registration path that this product cannot carry. */
  const connectorId = build.connection?.connectorId;
  if (!connectorId) {
    throw new Error('No connection was chosen for this agent, so there is nothing to '
      + 'register it against.');
  }
  /* Tagged explicitly: an untagged step has to be placed by inference, and
     inference put this under whatever stage happened to be current. */
  say(build.connection.provided
    ? `Bound to the Prove7 connection — ${build.connection.label}.`
    : `Bound to your connection — ${build.connection.label}.`,
  { stage: 'discovery' });

  /* Coded-SDK or proxied. The vendor agent id and the invocation pattern are
     proxied concepts — the agent the gateway dispatches to, and how — and a
     coded-SDK agent has neither: it runs in the customer's own process and
     calls the gateway itself, so the backend sets its id to the minted Agent
     UUID. Sending the tile's catalogue id for one of those would register it
     against an identifier that is not its own. The console branches on exactly
     this (ExteletRegistrationWizard Step2Connect), using the same predicate. */
  const coded = isCodedFramework(
    build.connection?.platformType || tile.platformType || '',
  );

  /* The invocation contract. Optional on the console's Connect step, optional
     here, and pre-filled with the console's own defaults — but an answered one
     has to reach the backend rather than being dropped for a literal. */
  const contract = build.agent || {};
  const num = (v, fallback) => {
    const n = Number(v);
    return Number.isFinite(n) && n > 0 ? n : fallback;
  };

  const res = await gelOnboardExteLet({
    name,
    platformConnectorId: connectorId,
    /* What the person typed, falling back to what the tile already knew. */
    externalAgentId: coded ? null : (contract.externalAgentId || tile.externalAgentId),
    externalAgentName: name,
    externalAgentVersion: String(contract.externalAgentVersion || '').trim() || '1.0.0',
    invocationPattern: coded ? null : (contract.invocationPattern || 'SYNCHRONOUS'),
    maxTokensPerRequest: num(contract.maxTokensPerRequest, 8192),
    timeoutSeconds: num(contract.timeoutSeconds, 300),
    integrationPath: integrationPathFor(build.path) || tile.integrationPath || 'CODED',
    accountability: intent,
    // Recorded on the agent so the inventory can tell a wizard agent from one somebody
    // registered by hand. The backend accepts only values it recognises, so this cannot
    // widen the facet by being wrong.
    creationSource: CREATION_SOURCE,
  });
  const envelope = (res && 'data' in res) ? res.data : res;
  const reg = envelope?.registration;
  if (!reg) throw new Error(res?.error || 'Could not register this agent.');

  /* The onboard clears four journey stages in one transaction. Each is reported
     as its own step, because the strip above the trail claims those stages are
     done and a strip that moves without the trail saying why asks somebody to
     take it on faith.

     The wording is held to what the code does. `ensureIdentity` stamps the
     SPIFFE name on the agent; the SVID is minted out of band once the SPIRE
     Workload API is wired, so this is an identity assigned and not a
     certificate issued. And the onboard stamps the integration path on the
     registration — it creates no Intent Profile binding, so this must not say
     that it did. */
  say(`Registered "${name}" and brought it under protection.`, {
    stage: 'discovery',
    detailText: 'It exists on the record now, with its own row and its own history.',
  });
  say('Stamped it with a name of its own.', {
    stage: 'identity',
    detailText: 'A SPIFFE name no other agent carries. Its certificate is minted when the '
      + 'workload first presents itself.',
  });
  say(`Recorded the path its calls must take — ${
    integrationPathFor(build.path) === 'MCP' ? 'the gateway' : 'the SDK'}.`, {
    /* Register, NOT Intent. This is the INTEGRATION path chosen at "How should
       calls reach it?" — SDK or gateway. The Intent Profile is a different
       thing entirely and is bound at its own step. Tagged 'intent' it filed
       itself under the Intent heading and made that stage look complete before
       the question had been asked. */
    stage: 'discovery',
    detailText: 'Stamped on the registration the gateway reads. A full Intent Profile '
      + 'binding is set on its own page in the platform.',
  });
  say(intent.ownerMode === 'nominate'
    ? 'Named its accountable owner. The record stays NOMINATED until they accept for themselves.'
    : 'Recorded you as its accountable owner, by name, in the audit trail.', {
    /* Register. Accountability is the last question before the commit; Access
       is role bindings across Org · Tenant · Instance, which is a later step
       with its own question. */
    stage: 'discovery',
  });
  const score = await scoreNow(reg.agentId, say);

  /* The envelope is kept, not dropped. Its keys are on the wire exactly once —
     only hashes are persisted and nothing returns them again — so a surface
     that registers an agent and discards them has handed somebody an agent
     they cannot wire up. The spine shows them in its next step. */
  return {
    kind: 'ExteLet',
    agentId: reg.agentId,
    registrationId: reg.id,
    name,
    score,
    envelope,
    integrationPath: integrationPathFor(build.path) || tile.integrationPath || 'CODED',
  };
}

/** Prose or a runbook: let the pipeline draft it. */
async function provisionAutoLet(described, proposedAgentName, actorName, say, build = {}) {
  const name = displayNameFor(build.naming?.name || proposedAgentName, actorName);
  /* The four dimensions the launcher sends as hints. Each maps to a column
     V188 adds with a CHECK constraint, so a value outside the enum is refused
     by the backend rather than stored — which is why they are surfaced as
     choices instead of left for the pipeline to invent. `auto` is deliberately
     not sent: omitting execution_mode is what lets the backend apply its own
     source-aware default. */
  const m = build.modes || {};
  const hints = {};
  if (name) hints.proposedAgentName = name;
  if (m.functionMode) hints.function_mode = m.functionMode;
  if (m.industryMode) hints.industry_mode = m.industryMode;
  if (m.operationMode) hints.operation_mode = m.operationMode;
  if (m.executionMode && m.executionMode !== 'auto') hints.execution_mode = m.executionMode;

  const res = await autoCreateAgent({
    sourceKind: 'description',
    sourceRef: build.words?.sourceRef || described,
    proposedAgentName: name || null,
    hints: Object.keys(hints).length ? hints : undefined,
  });
  const runId = res?.data?.runId;
  if (!runId) throw new Error(res?.error || 'The build did not start.');
  // From here the pipeline's own event stream owns the lane, so this is the last
  // thing narrated from the client — two producers writing the same feed at once
  // would interleave into nonsense.
  say('Started the build. Every step it takes appears here as it happens.');
  return { kind: 'AutoLet', runId, name };
}

/** An empty native agent the person will shape themselves. */
async function provisionLet(baseName, actorName, orgId, tenantId, say, accountability,
  build = {}) {
  // Same contract as the ExteLet onboard — CreateAgentRequest carries the intent
  // (CreateAgentRequest.java:154), and absent means DISCOVERED_UNOWNED.
  const intent = normalizeAccountability(accountability);
  const name = displayNameFor(build.naming?.name || baseName, actorName);
  const m = build.modes || {};
  const res = await createAgent({
    name,
    orgId,
    tenantId,
    /* The four dimensions, defaulted exactly as the create form defaults them.
       `trust_band` is NOT here: it is platform-written by the score waterfall,
       and user supply is rejected to keep the autonomous gate ungameable. */
    functionMode: m.functionMode || 'general',
    industryMode: m.industryMode || 'general',
    operationMode: m.operationMode || 'platform',
    executionMode: m.executionMode || 'deterministic',
    phase: 'build',
    status: 'active',
    ...(build.naming?.description?.trim()
      ? { description: build.naming.description.trim() } : {}),
    accountability: intent,
    // Same provenance stamp the ExteLet path sends. CreateAgentRequest now carries the
    // field; before it did so, Jackson dropped it silently (the DTO is
    // ignoreUnknown=true) and every wizard Let read as "Manual / authored".
    creationSource: CREATION_SOURCE,
  });
  const agent = res?.data || res;
  if (!agent?.id) throw new Error(res?.error || 'Could not create the agent.');
  say(intent.ownerMode === 'nominate'
    ? `Created "${name}" in your current scope. Its named owner is asked to accept; the record stays NOMINATED until they do.`
    : `Created "${name}" in your current scope, with you as its owner.`);
  const score = await scoreNow(agent.id, say);
  return { kind: 'Let', agentId: agent.id, name, score };
}

/**
 * The one entry point the wizard calls.
 *
 * @param {Object}   tile      an ESTATE_AGENTS entry, or null when the person typed
 * @param {string}   described the prose, when they typed
 * @param {Object}   scope     `{ orgId, tenantId }` — only a Let needs them
 * @param {string}   actorName the signed-in person's display name; the agent's name
 *                             is tagged with the first three letters of it
 * @param {Object}   accountability `{ownerMode, ownerPrincipalId, accepted}` — who answers
 *                             for it. Omitted means the creator owns and accepts inline,
 *                             which is what every caller did implicitly before the choice
 *                             could be expressed at all.
 * @param {Function} onStep    called with one `{id, at, text, refused}` per step that
 *                             has actually completed, for the live lane
 */
export async function provision({
  tile, described, scope, actorName, accountability, build, onStep,
}) {
  const say = narrator(onStep);
  if (tile?.archetype === 'extelet') {
    return provisionExteLet(tile, actorName, say, accountability, build);
  }
  if (tile?.archetype === 'let') {
    return provisionLet(tile.name, actorName, scope?.orgId, scope?.tenantId, say,
      accountability, build);
  }
  // Everything else — a runbook tile, or anything typed — is drafted.
  return provisionAutoLet(described || tile?.prose, tile?.name, actorName, say, build);
}

export default provision;
